// ──────────────────────────────────────────────────────────────────────
// Batch 5 R3 F2 — Person Detail Page (`/people/:user_id`).
//
// Header: avatar, name, role badge, "Reports to: X, Y", "Supervises: A, B",
// email mailto, join date, live clock-in v2 status.
// 5 tabs: Overview (KPI cards) / Tasks / EODs / Time Log / Activity.
//
// RBAC: server returns 403 unless caller is self / transitive supervisor /
// Owner / Admin / super_admin. Frontend renders a friendly 403 message.
// ──────────────────────────────────────────────────────────────────────
import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import {
  ArrowLeft, Mail, Calendar as CalIcon, Clock4, ShieldCheck, Crown, User as UserIcon,
  ClipboardList, FileText, Activity, ChevronUp, ChevronDown, Loader2, AlertTriangle,
} from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { personAPI, taskAPI, eodAPI } from '../lib/api';
import { useWorkspace } from '../contexts/WorkspaceContext';
import { toast } from 'sonner';

const ROLE_META = {
  super_admin: { label: 'Super Admin', Icon: Crown, color: 'text-purple-300', bg: 'bg-purple-500/10', border: 'border-purple-500/30' },
  owner:       { label: 'Owner',       Icon: Crown, color: 'text-gold',       bg: 'bg-gold/10',       border: 'border-gold/30' },
  admin:       { label: 'Admin',       Icon: ShieldCheck, color: 'text-amber-300', bg: 'bg-amber-500/10', border: 'border-amber-500/30' },
  manager:     { label: 'Manager',     Icon: UserIcon,    color: 'text-cyan-300',  bg: 'bg-cyan-500/10',  border: 'border-cyan-500/30' },
  team:        { label: 'Team',        Icon: UserIcon,    color: 'text-emerald-300', bg: 'bg-emerald-500/10', border: 'border-emerald-500/30' },
};

const TABS = [
  { id: 'overview', label: 'Overview', Icon: ChevronUp },
  { id: 'tasks',    label: 'Tasks',    Icon: ClipboardList },
  { id: 'eods',     label: 'EODs',     Icon: FileText },
  { id: 'time',     label: 'Time Log', Icon: Clock4 },
  { id: 'activity', label: 'Activity', Icon: Activity },
];

export default function PersonDetailPage() {
  const { user_id } = useParams();
  const navigate = useNavigate();
  const { currentWorkspace } = useWorkspace();
  const wsId = currentWorkspace?.id;
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');
  // CT-027 — tab unmount-on-switch with 200ms debounce. Rapid clicking
  // between tabs (e.g., Tasks → EODs → Time → Activity in <200ms) used
  // to fire one API request per click. Debouncing collapses those bursts
  // into a single switch + a single fetch. We render an "empty" state
  // (no tab) while pending so the previous tab unmounts immediately;
  // each tab component cleans up its own state on unmount.
  const [pendingTab, setPendingTab] = useState(null);
  const tabSwitchTimerRef = useRef(null);
  const switchTab = (next) => {
    if (next === activeTab) return;
    setPendingTab(next);
    if (tabSwitchTimerRef.current) clearTimeout(tabSwitchTimerRef.current);
    tabSwitchTimerRef.current = setTimeout(() => {
      setActiveTab(next);
      setPendingTab(null);
      tabSwitchTimerRef.current = null;
    }, 200);
  };
  useEffect(() => () => { if (tabSwitchTimerRef.current) clearTimeout(tabSwitchTimerRef.current); }, []);

  useEffect(() => {
    if (!user_id || !wsId) return;
    setLoading(true);
    setError(null);
    personAPI.detail(user_id, wsId)
      .then(res => setDetail(res.data))
      .catch(err => setError(err?.response?.status === 403 ? 'forbidden' : (err?.response?.data?.detail || 'Failed to load profile')))
      .finally(() => setLoading(false));
  }, [user_id, wsId]);

  // BUG-07 — refresh KPIs on global task-changed event + 15s poll fallback.
  useEffect(() => {
    if (!user_id || !wsId) return;
    const refetch = () => {
      personAPI.detail(user_id, wsId).then(res => setDetail(res.data)).catch(() => {});
    };
    window.addEventListener('stellar-task-changed', refetch);
    const pollId = setInterval(refetch, 15_000);
    return () => {
      window.removeEventListener('stellar-task-changed', refetch);
      clearInterval(pollId);
    };
  }, [user_id, wsId]);

  if (loading) {
    return <div className="text-center py-16 text-[12px] text-muted-foreground/50" data-testid="person-detail-loading"><Loader2 className="h-4 w-4 animate-spin inline mr-1.5" /> Loading profile…</div>;
  }
  if (error === 'forbidden') {
    return (
      <div className="max-w-2xl mx-auto p-8" data-testid="person-detail-forbidden">
        <Card className="bg-card border-amber-500/30">
          <CardContent className="p-6 text-center">
            <AlertTriangle className="h-8 w-8 text-amber-400 mx-auto mb-3" />
            <h2 className="text-[16px] font-display font-semibold text-foreground mb-1">Profile not visible to you</h2>
            <p className="text-[12px] text-muted-foreground/60 mb-4">
              You can only view profiles for yourself, your direct/transitive reports, or any workspace member if you're an Owner/Admin.
            </p>
            <Button variant="outline" size="sm" onClick={() => navigate(-1)}>Go back</Button>
          </CardContent>
        </Card>
      </div>
    );
  }
  if (error) {
    return <div className="text-center py-16 text-[12px] text-red-400/80" data-testid="person-detail-error">Error: {error}</div>;
  }
  if (!detail) return null;

  const meta = ROLE_META[detail.user.super_admin ? 'super_admin' : detail.user.role] || ROLE_META.team;
  const RoleIcon = meta.Icon;
  const initials = (detail.user.name || detail.user.email || '?')[0]?.toUpperCase();

  return (
    <div className="max-w-6xl mx-auto p-4 md:p-8" data-testid="person-detail-page">
      {/* Back */}
      <div className="flex items-center gap-3 mb-6">
        <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => navigate(-1)} aria-label="Back">
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <span className="text-[11px] text-muted-foreground/40">/ people / {detail.user.id.slice(0, 8)}…</span>
      </div>

      {/* HEADER CARD */}
      <Card className="bg-card border-border/30 mb-6">
        <CardContent className="p-6">
          <div className="flex flex-col md:flex-row gap-6">
            <div className="shrink-0">
              {detail.user.avatar_url ? (
                <img src={detail.user.avatar_url} alt="" className="h-20 w-20 rounded-full object-cover border-2 border-border/30" />
              ) : (
                <div className={`h-20 w-20 rounded-full ${meta.bg} flex items-center justify-center border-2 ${meta.border}`}>
                  <span className={`text-[28px] font-display font-semibold ${meta.color}`}>{initials}</span>
                </div>
              )}
            </div>
            <div className="flex-1 min-w-0 space-y-2">
              <div className="flex flex-wrap items-center gap-3">
                <h1 className="font-display text-[24px] font-semibold text-foreground" data-testid="person-detail-name">{detail.user.name || detail.user.email}</h1>
                <span className={`inline-flex items-center gap-1.5 px-2 py-1 rounded-md text-[11px] font-medium ${meta.bg} ${meta.color} border ${meta.border}`} data-testid="person-detail-role-badge">
                  <RoleIcon className="h-3 w-3" /> {meta.label}
                </span>
                <span className={`inline-flex items-center gap-1.5 px-2 py-1 rounded-md text-[11px] font-medium ${detail.live.is_clocked_in ? 'bg-emerald-500/10 text-emerald-300 border border-emerald-500/30' : 'bg-foreground/[0.03] text-muted-foreground/60 border border-border/30'}`} data-testid="person-detail-clock-status">
                  <span className={`h-1.5 w-1.5 rounded-full ${detail.live.is_clocked_in ? 'bg-emerald-400 animate-pulse' : 'bg-red-400'}`} />
                  {detail.live.is_clocked_in ? `Clocked in · ${(detail.live.today_total_minutes / 60).toFixed(1)}h today` : 'Not clocked in'}
                </span>
              </div>
              <div className="flex flex-wrap items-center gap-4 text-[12px] text-muted-foreground/60">
                <a href={`mailto:${detail.user.email}`} className="inline-flex items-center gap-1 hover:text-gold" data-testid="person-detail-email">
                  <Mail className="h-3 w-3" /> {detail.user.email}
                </a>
                {detail.user.joined_at && (
                  <span className="inline-flex items-center gap-1">
                    <CalIcon className="h-3 w-3" /> Joined {detail.user.joined_at.slice(0, 10)}
                  </span>
                )}
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-3 border-t border-border/20">
                <div>
                  <div className="text-[10px] uppercase tracking-wide text-muted-foreground/40 mb-1">Reports to</div>
                  {detail.reports_to.length === 0
                    ? <div className="text-[12px] text-muted-foreground/40 italic">No supervisors</div>
                    : <div className="flex flex-wrap gap-1.5" data-testid="person-detail-reports-to">{detail.reports_to.map(p => (
                        <Link key={p.id} to={`/people/${p.id}`} className="inline-flex items-center gap-1 text-[11px] px-1.5 py-0.5 rounded bg-foreground/[0.05] hover:bg-gold/10 text-foreground/80 hover:text-gold">
                          {p.name}
                        </Link>
                      ))}</div>}
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-wide text-muted-foreground/40 mb-1">Supervises</div>
                  {detail.supervises.length === 0
                    ? <div className="text-[12px] text-muted-foreground/40 italic">No reports</div>
                    : <div className="flex flex-wrap gap-1.5" data-testid="person-detail-supervises">{detail.supervises.map(p => (
                        <Link key={p.id} to={`/people/${p.id}`} className="inline-flex items-center gap-1 text-[11px] px-1.5 py-0.5 rounded bg-foreground/[0.05] hover:bg-gold/10 text-foreground/80 hover:text-gold">
                          {p.name}
                        </Link>
                      ))}</div>}
                </div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* TABS */}
      <div className="border-b border-border/30 mb-6 flex gap-1 overflow-x-auto">
        {TABS.map(tab => {
          const Icon = tab.Icon;
          const visualActive = (pendingTab || activeTab) === tab.id;
          return (
            <button
              key={tab.id}
              onClick={() => switchTab(tab.id)}
              className={`px-3 py-2 text-[12px] font-medium border-b-2 transition-colors inline-flex items-center gap-1.5 whitespace-nowrap ${visualActive ? 'border-gold text-gold' : 'border-transparent text-muted-foreground/60 hover:text-foreground'}`}
              data-testid={`person-tab-${tab.id}`}
            >
              <Icon className="h-3.5 w-3.5" /> {tab.label}
            </button>
          );
        })}
      </div>

      {/* TAB CONTENT — debounced switch: while a switch is pending, render a
          tiny placeholder so the previous tab unmounts immediately and its
          API calls are not chained. */}
      {pendingTab ? (
        <div className="text-center py-12 text-[12px] text-muted-foreground/40" data-testid="person-tab-switching">
          <Loader2 className="h-4 w-4 animate-spin inline mr-1.5" /> Switching to {pendingTab}…
        </div>
      ) : (
        <>
          {activeTab === 'overview' && <OverviewTab detail={detail} />}
          {activeTab === 'tasks'    && <TasksTab userId={user_id} wsId={wsId} />}
          {activeTab === 'eods'     && <EodsTab userId={user_id} wsId={wsId} />}
          {activeTab === 'time'     && <TimeLogTab userId={user_id} wsId={wsId} />}
          {activeTab === 'activity' && <ActivityTab userId={user_id} wsId={wsId} />}
        </>
      )}
    </div>
  );
}

function OverviewTab({ detail }) {
  const kpis = detail.kpis || {};
  const v = kpis.velocity;
  const flagOn = !!(detail.flags || {}).flag_velocity;
  // F3 — when velocity payload is present, surface a real metric. Falls back
  // to a friendly placeholder when the flag is off or no batches exist yet.
  const velocityValue = v && v.avg_minutes_per_task != null
    ? `${v.avg_minutes_per_task}m`
    : (flagOn ? 'No batches yet' : 'Coming soon');
  const velocityHint = v && v.top_deliverable_type
    ? `avg/${v.top_deliverable_type} · ${v.batches_count} batch${v.batches_count === 1 ? '' : 'es'}`
    : (flagOn ? 'Complete ≥2 same-type tasks in one clock-in session' : 'F3 Velocity Engine ships this metric');
  const onTimeRate = v && v.on_time_rate_pct != null ? `${v.on_time_rate_pct}%` : null;
  const cards = [
    { label: 'Active tasks',          value: kpis.active_tasks ?? '—',          color: 'text-cyan-300',    icon: ClipboardList },
    { label: 'Completed (last 30d)',  value: kpis.completed_last_30d ?? '—',    color: 'text-emerald-300', icon: ChevronUp },
    { label: 'EODs (last 14d)',       value: kpis.eods_submitted_last_14d ?? '—', color: 'text-amber-300', icon: FileText },
    {
      label: 'Velocity',
      value: velocityValue,
      color: (v && v.avg_minutes_per_task != null) ? 'text-gold' : 'text-muted-foreground/60',
      icon: Activity,
      hint: velocityHint,
      sub: onTimeRate ? `On-time ${onTimeRate}` : null,
      placeholder: !(v && v.avg_minutes_per_task != null),
    },
  ];
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3" data-testid="person-overview-kpis">
      {cards.map(c => {
        const Icon = c.icon;
        return (
          <Card key={c.label} className="bg-card border-border/30">
            <CardContent className="p-4">
              <div className="flex items-center justify-between mb-1">
                <span className="text-[10px] uppercase tracking-wide text-muted-foreground/40">{c.label}</span>
                <Icon className="h-3.5 w-3.5 text-muted-foreground/30" />
              </div>
              <div
                className={`text-[24px] font-display font-semibold ${c.color}`}
                data-testid={c.label === 'Velocity' ? 'person-kpi-velocity' : undefined}
              >
                {c.value}
              </div>
              {c.sub && <div className="text-[11px] text-emerald-300/70 mt-0.5">{c.sub}</div>}
              {c.hint && <div className={`text-[10px] mt-0.5 ${c.placeholder ? 'text-amber-300/60' : 'text-muted-foreground/40'}`}>{c.hint}</div>}
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
}

function TasksTab({ userId, wsId }) {
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    if (!userId || !wsId) return;
    setLoading(true);
    taskAPI.list(wsId, { assigned_to: userId, limit: 50 })
      .then(res => setTasks(res.data?.tasks || []))
      .catch(() => toast.error('Failed to load tasks'))
      .finally(() => setLoading(false));
  }, [userId, wsId]);
  if (loading) return <Loading />;
  if (!tasks.length) return <Empty label="No tasks assigned to this person." />;
  return (
    <Card className="bg-card border-border/30">
      <CardContent className="p-0">
        <table className="w-full text-[12px]" data-testid="person-tasks-table">
          <thead>
            <tr className="border-b border-border/20">
              <th className="text-left px-3 py-2 text-[10px] uppercase text-muted-foreground/40">Title</th>
              <th className="text-left px-3 py-2 text-[10px] uppercase text-muted-foreground/40">Status</th>
              <th className="text-left px-3 py-2 text-[10px] uppercase text-muted-foreground/40">Priority</th>
              <th className="text-left px-3 py-2 text-[10px] uppercase text-muted-foreground/40">Due</th>
            </tr>
          </thead>
          <tbody>
            {tasks.map(t => (
              <tr key={t.id} className="border-b border-border/10 hover:bg-foreground/[0.02]">
                <td className="px-3 py-2 text-foreground/90 truncate max-w-[400px]">{t.title}</td>
                <td className="px-3 py-2 text-muted-foreground/70">{t.status}</td>
                <td className="px-3 py-2 text-muted-foreground/70">{t.priority || '—'}</td>
                <td className="px-3 py-2 text-muted-foreground/70">{t.due_date?.slice(0, 10) || '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </CardContent>
    </Card>
  );
}

function EodsTab({ userId, wsId }) {
  const [eods, setEods] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    if (!userId || !wsId) return;
    setLoading(true);
    const from = new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString().slice(0, 10);
    const to = new Date().toISOString().slice(0, 10);
    eodAPI.list(wsId, userId, from, to)
      .then(res => setEods(res.data?.eods || []))
      .catch(() => toast.error('Failed to load EODs'))
      .finally(() => setLoading(false));
  }, [userId, wsId]);
  if (loading) return <Loading />;
  if (!eods.length) return <Empty label="No EODs submitted in the last 30 days." />;
  return (
    <div className="space-y-3" data-testid="person-eods-list">
      {eods.map(e => (
        <Card key={e.id} className="bg-card border-border/30">
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-[13px] font-display font-semibold text-foreground">{e.report_date}</span>
              {e.hours_worked != null && <span className="text-[11px] text-muted-foreground/50">{e.hours_worked}h logged</span>}
            </div>
            <div className="space-y-2 text-[12px]">
              {e.what_i_did && <div><div className="text-[10px] uppercase tracking-wide text-muted-foreground/40">What I did</div><div className="text-foreground/90 whitespace-pre-wrap">{e.what_i_did}</div></div>}
              {e.blockers && <div><div className="text-[10px] uppercase tracking-wide text-amber-400/60">Blockers</div><div className="text-amber-300/90 whitespace-pre-wrap">{e.blockers}</div></div>}
              {e.tomorrow_plan && <div><div className="text-[10px] uppercase tracking-wide text-muted-foreground/40">Tomorrow plan</div><div className="text-foreground/90 whitespace-pre-wrap">{e.tomorrow_plan}</div></div>}
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}

function TimeLogTab({ userId, wsId }) {
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    if (!userId || !wsId) return;
    setLoading(true);
    personAPI.timeLog(userId, wsId, 100)
      .then(res => setSessions(res.data?.sessions || []))
      .catch(() => toast.error('Failed to load time log'))
      .finally(() => setLoading(false));
  }, [userId, wsId]);
  if (loading) return <Loading />;
  if (!sessions.length) return <Empty label="No clock sessions recorded." />;
  return (
    <Card className="bg-card border-border/30" data-testid="person-time-log-table">
      <CardContent className="p-0">
        <table className="w-full text-[12px]">
          <thead>
            <tr className="border-b border-border/20">
              <th className="text-left px-3 py-2 text-[10px] uppercase text-muted-foreground/40">Clock In</th>
              <th className="text-left px-3 py-2 text-[10px] uppercase text-muted-foreground/40">Clock Out</th>
              <th className="text-right px-3 py-2 text-[10px] uppercase text-muted-foreground/40">Duration</th>
            </tr>
          </thead>
          <tbody>
            {sessions.map(s => (
              <tr key={s.id} className="border-b border-border/10 hover:bg-foreground/[0.02]">
                <td className="px-3 py-2 text-foreground/90">{(s.clock_in_at || '').replace('T', ' ').slice(0, 16)}</td>
                <td className="px-3 py-2 text-muted-foreground/70">{s.clock_out_at ? s.clock_out_at.replace('T', ' ').slice(0, 16) : <span className="text-emerald-400">— Active —</span>}</td>
                <td className="px-3 py-2 text-right text-foreground/90">{s.total_minutes != null ? `${(s.total_minutes / 60).toFixed(2)}h` : '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </CardContent>
    </Card>
  );
}

function ActivityTab({ userId, wsId }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    if (!userId || !wsId) return;
    setLoading(true);
    personAPI.activity(userId, wsId, 50)
      .then(res => setItems(res.data?.activities || []))
      .catch(() => toast.error('Failed to load activity'))
      .finally(() => setLoading(false));
  }, [userId, wsId]);
  if (loading) return <Loading />;
  if (!items.length) return <Empty label="No recent activity." />;
  return (
    <div className="space-y-2" data-testid="person-activity-list">
      {items.map(a => (
        <Card key={a.id} className="bg-card border-border/30">
          <CardContent className="p-3 flex items-start gap-3">
            <Activity className="h-4 w-4 text-muted-foreground/40 mt-0.5 shrink-0" />
            <div className="flex-1 min-w-0">
              <div className="text-[12px] text-foreground/90"><span className="font-medium text-foreground">{a.event_type || a.type || 'event'}</span> {a.description || a.summary || ''}</div>
              <div className="text-[10px] text-muted-foreground/40">{(a.created_at || '').replace('T', ' ').slice(0, 16)}</div>
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}

function Loading() {
  return <div className="text-center py-8 text-[12px] text-muted-foreground/50"><Loader2 className="h-4 w-4 animate-spin inline mr-1.5" /> Loading…</div>;
}
function Empty({ label }) {
  return <div className="text-center py-8 text-[12px] text-muted-foreground/40 italic">{label}</div>;
}
